import React, { useState, useRef, useCallback } from 'react';
import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ReferenceArea,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { Download, SlidersHorizontal, ChevronDown, ChevronUp, X, ArrowRight, Clock, Layers, Wrench, TrendingUp } from 'lucide-react';
import { OptimizationOpportunity } from '../../services/processOptimizationAPI';

// ── Types ────────────────────────────────────────────────────────────────────

interface ComplexityWeights {
  governance: number;      // governance_complexity_score
  integration: number;     // integration_complexity_score
  regulatory: number;      // regulatory_risk_score
  implComplexity: number;  // implementation_complexity (low/med/high)
  prerequisites: number;   // prerequisites count
}

interface ValueWeights {
  automation: number;      // automation_readiness_score
  timeReduction: number;   // estimated_time_reduction
  stepsAffected: number;   // affected_steps.length
  workflowCount: number;   // workflow_count
}

interface PlotPoint {
  id: number;
  name: string;
  x: number;  // complexity 0-100
  y: number;  // value 0-100
  size: number;
  quadrant: 'quick_win' | 'strategic_bet' | 'fill_in' | 'reconsider';
  opportunity: OptimizationOpportunity;
}

interface PrioritizationMatrixProps {
  opportunities: OptimizationOpportunity[];
  onOpportunityClick: (id: number) => void;
}

// ── Score Calculation ─────────────────────────────────────────────────────────

function normalizeToHundred(value: number, min: number, max: number): number {
  if (max === min) return 50;
  return Math.min(100, Math.max(0, ((value - min) / (max - min)) * 100));
}

function parseTimeReduction(estimated: string | undefined): number {
  if (!estimated) return 0;
  const match = estimated.match(/(\d+)/);
  return match ? Math.min(100, parseInt(match[1])) : 0;
}

function implComplexityToScore(complexity: string | undefined): number {
  if (complexity === 'low') return 25;
  if (complexity === 'high') return 75;
  return 50; // medium
}

function calculateComplexityScore(opp: OptimizationOpportunity, w: ComplexityWeights): number {
  const govScore = normalizeToHundred(opp.governance_complexity_score ?? 5, 1, 10);
  const intScore = normalizeToHundred(opp.integration_complexity_score ?? 5, 1, 10);
  const regScore = normalizeToHundred(opp.regulatory_risk_score ?? 5, 1, 10);
  const implScore = implComplexityToScore(opp.implementation_complexity);
  const prereqCount = opp.dependencies?.prerequisites?.length ?? 0;
  const prereqScore = normalizeToHundred(prereqCount, 0, 5);

  return (
    w.governance * govScore +
    w.integration * intScore +
    w.regulatory * regScore +
    w.implComplexity * implScore +
    w.prerequisites * prereqScore
  );
}

function calculateValueScore(opp: OptimizationOpportunity, w: ValueWeights): number {
  const autoScore = normalizeToHundred(opp.automation_readiness_score ?? 5, 1, 10);
  const timeScore = parseTimeReduction(opp.estimated_time_reduction);
  const stepsScore = normalizeToHundred((opp.affected_steps?.length ?? 1), 1, 20);
  const workflowScore = normalizeToHundred(opp.workflow_count ?? 1, 1, 10);

  return (
    w.automation * autoScore +
    w.timeReduction * timeScore +
    w.stepsAffected * stepsScore +
    w.workflowCount * workflowScore
  );
}

function classifyQuadrant(value: number, complexity: number): PlotPoint['quadrant'] {
  if (value >= 50 && complexity < 50) return 'quick_win';
  if (value >= 50 && complexity >= 50) return 'strategic_bet';
  if (value < 50 && complexity < 50) return 'fill_in';
  return 'reconsider';
}

const QUADRANT_COLORS: Record<PlotPoint['quadrant'], string> = {
  quick_win: '#38a169',
  strategic_bet: '#3182ce',
  fill_in: '#718096',
  reconsider: '#e53e3e',
};

const QUADRANT_LABELS: Record<PlotPoint['quadrant'], string> = {
  quick_win: 'Quick Win',
  strategic_bet: 'Strategic Bet',
  fill_in: 'Fill-In',
  reconsider: 'Reconsider',
};

// ── Custom Dot ────────────────────────────────────────────────────────────────

const CustomDot = (props: any) => {
  const { cx, cy, payload, hoveredId, selectedId, onHover, onClick } = props;
  const isHovered = hoveredId === payload.id;
  const isSelected = selectedId === payload.id;
  const r = Math.max(8, Math.min(20, 8 + (payload.size ?? 1) * 1.5));
  const displayR = isSelected ? r + 4 : isHovered ? r + 2 : r;

  return (
    <g>
      {isSelected && (
        <circle
          cx={cx}
          cy={cy}
          r={displayR + 5}
          fill="none"
          stroke={QUADRANT_COLORS[payload.quadrant as PlotPoint['quadrant']]}
          strokeWidth={2}
          strokeOpacity={0.4}
        />
      )}
      <circle
        cx={cx}
        cy={cy}
        r={displayR}
        fill={QUADRANT_COLORS[payload.quadrant as PlotPoint['quadrant']]}
        fillOpacity={isSelected ? 1 : 0.85}
        stroke={isSelected ? '#1a202c' : isHovered ? '#4a5568' : '#fff'}
        strokeWidth={isSelected ? 3 : isHovered ? 2 : 1.5}
        style={{ cursor: 'pointer', transition: 'r 0.15s ease' }}
        onMouseEnter={() => onHover(payload.id)}
        onMouseLeave={() => onHover(null)}
        onClick={() => onClick(payload.id)}
      />
    </g>
  );
};

// ── Custom Tooltip ────────────────────────────────────────────────────────────

const CustomTooltip = ({ active, payload }: any) => {
  if (!active || !payload?.[0]) return null;
  const d: PlotPoint = payload[0].payload;
  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-xl p-4 max-w-xs">
      <p className="font-bold text-gray-900 text-sm mb-2 leading-tight">{d.name}</p>
      <div className="flex items-center gap-2 mb-3">
        <span
          className="px-2 py-0.5 rounded-full text-xs font-semibold text-white"
          style={{ backgroundColor: QUADRANT_COLORS[d.quadrant] }}
        >
          {QUADRANT_LABELS[d.quadrant]}
        </span>
      </div>
      <div className="space-y-1 text-xs text-gray-600">
        <div className="flex justify-between">
          <span>Business Value</span>
          <span className="font-semibold text-gray-900">{d.y.toFixed(0)}/100</span>
        </div>
        <div className="flex justify-between">
          <span>Implementation Complexity</span>
          <span className="font-semibold text-gray-900">{d.x.toFixed(0)}/100</span>
        </div>
        <div className="flex justify-between">
          <span>Steps Affected</span>
          <span className="font-semibold text-gray-900">{d.opportunity.affected_steps?.length ?? 0}</span>
        </div>
        <div className="flex justify-between">
          <span>Complexity</span>
          <span className="font-semibold capitalize text-gray-900">{d.opportunity.implementation_complexity ?? 'N/A'}</span>
        </div>
      </div>
      <p className="mt-2 text-xs text-indigo-500 font-medium">Click bubble to pin details</p>
    </div>
  );
};

// ── Weight Slider ─────────────────────────────────────────────────────────────

const WeightSlider = ({
  label, value, onChange,
}: { label: string; value: number; onChange: (v: number) => void }) => (
  <div className="flex items-center gap-3">
    <span className="text-xs text-gray-600 w-40 shrink-0">{label}</span>
    <input
      type="range"
      min={0}
      max={1}
      step={0.05}
      value={value}
      onChange={e => onChange(parseFloat(e.target.value))}
      className="flex-1 accent-indigo-600"
    />
    <span className="text-xs font-mono text-gray-900 w-10 text-right">{(value * 100).toFixed(0)}%</span>
  </div>
);

// ── Main Component ────────────────────────────────────────────────────────────

const PrioritizationMatrix: React.FC<PrioritizationMatrixProps> = ({
  opportunities,
  onOpportunityClick,
}) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const [hoveredId, setHoveredId] = useState<number | null>(null);
  const [selectedPoint, setSelectedPoint] = useState<PlotPoint | null>(null);
  const [showWeights, setShowWeights] = useState(false);

  const [complexityWeights, setComplexityWeights] = useState<ComplexityWeights>({
    governance: 0.30,
    integration: 0.25,
    regulatory: 0.20,
    implComplexity: 0.15,
    prerequisites: 0.10,
  });

  const [valueWeights, setValueWeights] = useState<ValueWeights>({
    automation: 0.35,
    timeReduction: 0.30,
    stepsAffected: 0.20,
    workflowCount: 0.15,
  });

  // Build plot data
  const plotData: PlotPoint[] = opportunities.map(opp => {
    const x = calculateComplexityScore(opp, complexityWeights);
    const y = calculateValueScore(opp, valueWeights);
    return {
      id: opp.id,
      name: opp.title,
      x: Math.round(x),
      y: Math.round(y),
      size: opp.affected_steps?.length ?? 1,
      quadrant: classifyQuadrant(y, x),
      opportunity: opp,
    };
  });

  // Quadrant counts
  const counts = {
    quick_win: plotData.filter(d => d.quadrant === 'quick_win').length,
    strategic_bet: plotData.filter(d => d.quadrant === 'strategic_bet').length,
    fill_in: plotData.filter(d => d.quadrant === 'fill_in').length,
    reconsider: plotData.filter(d => d.quadrant === 'reconsider').length,
  };

  // Grounded count
  const groundedCount = opportunities.filter(o => (o as any).data_source === 'grounded').length;

  // Update a single complexity weight
  const updateComplexityWeight = useCallback((key: keyof ComplexityWeights, value: number) => {
    setComplexityWeights(prev => ({ ...prev, [key]: value }));
  }, []);

  // Update a single value weight
  const updateValueWeight = useCallback((key: keyof ValueWeights, value: number) => {
    setValueWeights(prev => ({ ...prev, [key]: value }));
  }, []);

  // Export PNG
  const handleExport = async () => {
    if (!chartRef.current) return;
    try {
      const html2canvas = (await import('html2canvas')).default;
      const canvas = await html2canvas(chartRef.current, { backgroundColor: '#fff', scale: 2 });
      const link = document.createElement('a');
      link.download = 'prioritization-matrix.png';
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (e) {
      console.error('Export failed:', e);
    }
  };

  const complexitySum = Object.values(complexityWeights).reduce((a, b) => a + b, 0);
  const valueSum = Object.values(valueWeights).reduce((a, b) => a + b, 0);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-bold text-gray-900">Prioritization Matrix</h2>
            <span className="px-2.5 py-1 bg-green-100 text-green-800 text-xs font-bold rounded-full">
              {counts.quick_win} Quick Win{counts.quick_win !== 1 ? 's' : ''}
            </span>
            {groundedCount > 0 && (
              <span className="px-2.5 py-1 bg-emerald-100 text-emerald-800 text-xs font-semibold rounded-full">
                {groundedCount} Grounded / {opportunities.length - groundedCount} Estimated
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowWeights(v => !v)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-lg hover:bg-indigo-100 transition-colors"
            >
              <SlidersHorizontal className="w-4 h-4" />
              Adjust Weights
              {showWeights ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            </button>
            <button
              onClick={handleExport}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Download className="w-4 h-4" />
              Export PNG
            </button>
          </div>
        </div>

        {/* Weight adjustment panel */}
        {showWeights && (
          <div className="mt-4 pt-4 border-t border-gray-100 grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-bold text-gray-700 uppercase tracking-wide">Complexity Weights</p>
                <span className={`text-xs font-mono ${Math.abs(complexitySum - 1) > 0.01 ? 'text-red-500' : 'text-green-600'}`}>
                  Sum: {(complexitySum * 100).toFixed(0)}%
                </span>
              </div>
              <div className="space-y-2">
                <WeightSlider label="Governance Complexity" value={complexityWeights.governance} onChange={v => updateComplexityWeight('governance', v)} />
                <WeightSlider label="Integration Complexity" value={complexityWeights.integration} onChange={v => updateComplexityWeight('integration', v)} />
                <WeightSlider label="Regulatory Risk" value={complexityWeights.regulatory} onChange={v => updateComplexityWeight('regulatory', v)} />
                <WeightSlider label="Impl. Complexity" value={complexityWeights.implComplexity} onChange={v => updateComplexityWeight('implComplexity', v)} />
                <WeightSlider label="Prerequisites Count" value={complexityWeights.prerequisites} onChange={v => updateComplexityWeight('prerequisites', v)} />
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-bold text-gray-700 uppercase tracking-wide">Value Weights</p>
                <span className={`text-xs font-mono ${Math.abs(valueSum - 1) > 0.01 ? 'text-red-500' : 'text-green-600'}`}>
                  Sum: {(valueSum * 100).toFixed(0)}%
                </span>
              </div>
              <div className="space-y-2">
                <WeightSlider label="Automation Readiness" value={valueWeights.automation} onChange={v => updateValueWeight('automation', v)} />
                <WeightSlider label="Time Reduction" value={valueWeights.timeReduction} onChange={v => updateValueWeight('timeReduction', v)} />
                <WeightSlider label="Steps Affected" value={valueWeights.stepsAffected} onChange={v => updateValueWeight('stepsAffected', v)} />
                <WeightSlider label="Workflow Count" value={valueWeights.workflowCount} onChange={v => updateValueWeight('workflowCount', v)} />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Chart */}
      <div ref={chartRef} className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
        <ResponsiveContainer width="100%" height={500}>
          <ScatterChart margin={{ top: 20, right: 40, bottom: 60, left: 60 }}>
            {/* Quadrant backgrounds */}
            <ReferenceArea x1={0} x2={50} y1={50} y2={100} fill="#f0fff4" fillOpacity={0.7} />
            <ReferenceArea x1={50} x2={100} y1={50} y2={100} fill="#ebf8ff" fillOpacity={0.7} />
            <ReferenceArea x1={0} x2={50} y1={0} y2={50} fill="#f7fafc" fillOpacity={0.7} />
            <ReferenceArea x1={50} x2={100} y1={0} y2={50} fill="#fff5f5" fillOpacity={0.7} />

            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />

            {/* Quadrant dividers */}
            <ReferenceLine x={50} stroke="#94a3b8" strokeDasharray="6 3" strokeWidth={1.5} />
            <ReferenceLine y={50} stroke="#94a3b8" strokeDasharray="6 3" strokeWidth={1.5} />

            <XAxis
              type="number"
              dataKey="x"
              domain={[0, 100]}
              label={{ value: 'Implementation Complexity →', position: 'bottom', offset: -10, fontSize: 12, fill: '#64748b' }}
              tick={{ fontSize: 11, fill: '#94a3b8' }}
            />
            <YAxis
              type="number"
              dataKey="y"
              domain={[0, 100]}
              label={{ value: '← Business Value', angle: -90, position: 'insideLeft', offset: -10, fontSize: 12, fill: '#64748b' }}
              tick={{ fontSize: 11, fill: '#94a3b8' }}
            />

            <Tooltip content={<CustomTooltip />} />

            <Scatter
              data={plotData}
              shape={(props: any) => (
                <CustomDot
                  {...props}
                  hoveredId={hoveredId}
                  selectedId={selectedPoint?.id ?? null}
                  onHover={setHoveredId}
                  onClick={(id: number) => {
                    const pt = plotData.find(p => p.id === id) ?? null;
                    setSelectedPoint(prev => prev?.id === id ? null : pt);
                  }}
                />
              )}
            >
              {plotData.map(entry => (
                <Cell key={entry.id} fill={QUADRANT_COLORS[entry.quadrant]} />
              ))}
            </Scatter>
          </ScatterChart>
        </ResponsiveContainer>

        {/* Quadrant labels overlay */}
        <div className="grid grid-cols-2 gap-2 mt-2 text-center">
          <div className="text-xs font-semibold text-green-700 bg-green-50 rounded-lg py-3">Quick Wins ({counts.quick_win})</div>
          <div className="text-xs font-semibold text-blue-700 bg-blue-50 rounded-lg py-3">Strategic Bets ({counts.strategic_bet})</div>
          <div className="text-xs font-semibold text-gray-600 bg-gray-100 rounded-lg py-3">Fill-Ins ({counts.fill_in})</div>
          <div className="text-xs font-semibold text-red-700 bg-red-50 rounded-lg py-3">Reconsider ({counts.reconsider})</div>
        </div>
      </div>

      {/* Selected Opportunity Detail Panel */}
      {selectedPoint && (
        <div
          className="bg-white border-2 rounded-xl shadow-lg overflow-hidden"
          style={{ borderColor: QUADRANT_COLORS[selectedPoint.quadrant] }}
        >
          {/* Panel Header */}
          <div
            className="flex items-start justify-between px-5 py-4"
            style={{ backgroundColor: QUADRANT_COLORS[selectedPoint.quadrant] + '18' }}
          >
            <div className="flex-1 min-w-0 pr-3">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <span
                  className="px-2.5 py-0.5 rounded-full text-xs font-bold text-white"
                  style={{ backgroundColor: QUADRANT_COLORS[selectedPoint.quadrant] }}
                >
                  {QUADRANT_LABELS[selectedPoint.quadrant]}
                </span>
                <span className="text-xs text-gray-500 capitalize">
                  {selectedPoint.opportunity.inefficiency_type?.replace(/_/g, ' ')}
                </span>
              </div>
              <h3 className="text-base font-bold text-gray-900 leading-snug">{selectedPoint.name}</h3>
            </div>
            <button
              onClick={() => setSelectedPoint(null)}
              className="text-gray-400 hover:text-gray-600 flex-shrink-0 mt-0.5"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Panel Body */}
          <div className="px-5 py-4">
            {/* Score pills */}
            <div className="flex gap-3 mb-4">
              <div className="flex-1 bg-indigo-50 rounded-lg px-4 py-2.5 text-center">
                <p className="text-xs text-indigo-500 font-medium mb-0.5">Business Value</p>
                <p className="text-xl font-bold text-indigo-700">{selectedPoint.y}<span className="text-xs font-normal text-indigo-400">/100</span></p>
              </div>
              <div className="flex-1 bg-orange-50 rounded-lg px-4 py-2.5 text-center">
                <p className="text-xs text-orange-500 font-medium mb-0.5">Complexity</p>
                <p className="text-xl font-bold text-orange-700">{selectedPoint.x}<span className="text-xs font-normal text-orange-400">/100</span></p>
              </div>
            </div>

            {/* Key metrics row */}
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className="flex items-center gap-2 text-sm">
                <Layers className="w-4 h-4 text-gray-400 flex-shrink-0" />
                <div>
                  <p className="text-xs text-gray-500">Steps Affected</p>
                  <p className="font-semibold text-gray-800">{selectedPoint.opportunity.affected_steps?.length ?? 0}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Clock className="w-4 h-4 text-gray-400 flex-shrink-0" />
                <div>
                  <p className="text-xs text-gray-500">Time Reduction</p>
                  <p className="font-semibold text-gray-800">{selectedPoint.opportunity.estimated_time_reduction || 'N/A'}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <TrendingUp className="w-4 h-4 text-gray-400 flex-shrink-0" />
                <div>
                  <p className="text-xs text-gray-500">Effort Level</p>
                  <p className="font-semibold text-gray-800 capitalize">{selectedPoint.opportunity.implementation_complexity || 'N/A'}</p>
                </div>
              </div>
            </div>

            {/* Description */}
            {selectedPoint.opportunity.description && (
              <p className="text-sm text-gray-600 leading-relaxed mb-4 line-clamp-3">
                {selectedPoint.opportunity.description}
              </p>
            )}

            {/* Tools */}
            {selectedPoint.opportunity.primary_tools && selectedPoint.opportunity.primary_tools.length > 0 && (
              <div className="flex items-start gap-2 mb-4">
                <Wrench className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                <div className="flex flex-wrap gap-1.5">
                  {selectedPoint.opportunity.primary_tools.slice(0, 5).map((tool, i) => (
                    <span key={i} className="px-2 py-0.5 bg-gray-100 text-gray-700 text-xs rounded-md font-medium">
                      {tool}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* View full details button */}
            <button
              onClick={() => onOpportunityClick(selectedPoint.id)}
              className="flex items-center gap-2 text-sm font-semibold text-white px-4 py-2 rounded-lg transition-colors"
              style={{ backgroundColor: QUADRANT_COLORS[selectedPoint.quadrant] }}
            >
              View Full Details
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Quadrant Summary Table */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
        <div className="px-5 py-3 border-b border-gray-100">
          <h3 className="text-sm font-bold text-gray-800">Quadrant Summary</h3>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50">
              <th className="text-left px-5 py-2 text-xs font-semibold text-gray-500 uppercase">Quadrant</th>
              <th className="text-left px-5 py-2 text-xs font-semibold text-gray-500 uppercase">Count</th>
              <th className="text-left px-5 py-2 text-xs font-semibold text-gray-500 uppercase">Recommendation</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            <tr>
              <td className="px-5 py-3">
                <span className="inline-flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-green-500 inline-block" />
                  <span className="font-medium text-green-800">Quick Wins</span>
                </span>
              </td>
              <td className="px-5 py-3 font-bold text-gray-900">{counts.quick_win}</td>
              <td className="px-5 py-3 text-gray-600">Prioritize immediately — high value, low effort</td>
            </tr>
            <tr>
              <td className="px-5 py-3">
                <span className="inline-flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-blue-500 inline-block" />
                  <span className="font-medium text-blue-800">Strategic Bets</span>
                </span>
              </td>
              <td className="px-5 py-3 font-bold text-gray-900">{counts.strategic_bet}</td>
              <td className="px-5 py-3 text-gray-600">Plan carefully — high value but requires investment</td>
            </tr>
            <tr>
              <td className="px-5 py-3">
                <span className="inline-flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-gray-400 inline-block" />
                  <span className="font-medium text-gray-700">Fill-Ins</span>
                </span>
              </td>
              <td className="px-5 py-3 font-bold text-gray-900">{counts.fill_in}</td>
              <td className="px-5 py-3 text-gray-600">Schedule when capacity allows</td>
            </tr>
            <tr>
              <td className="px-5 py-3">
                <span className="inline-flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-red-500 inline-block" />
                  <span className="font-medium text-red-800">Reconsider</span>
                </span>
              </td>
              <td className="px-5 py-3 font-bold text-gray-900">{counts.reconsider}</td>
              <td className="px-5 py-3 text-gray-600">Deprioritize or redesign — low value, high effort</td>
            </tr>
          </tbody>
        </table>
      </div>

      {opportunities.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          No opportunities available to display in matrix.
        </div>
      )}
    </div>
  );
};

export default PrioritizationMatrix;
